import * as React from 'react';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { Dialog, DialogContent } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { getToken } from '../../../core/api-client';
import { formatDateTime } from '../../../design-system/format';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { statusPill, statusDot } from '../../../design-system/visual';
import { monitoringApi } from '../api';
import type { Screenshot } from '../types';

interface Props {
  screenshot: Screenshot;
  /** When provided, a delete control is shown (owner/manager view only). */
  onDeleted?: () => void;
}

function useAuthBlobUrl(screenshotId: string): { url: string | null; loading: boolean; error: string | null } {
  const [url, setUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    let revoked: string | null = null;
    setLoading(true); setError(null);

    (async () => {
      try {
        const res = await fetch(`/api/monitoring/screenshots/${screenshotId}/file`, {
          headers: { Authorization: `Bearer ${getToken() ?? ''}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        const u = URL.createObjectURL(blob);
        revoked = u;
        setUrl(u);
      } catch (err: any) {
        if (!cancelled) setError(err.message ?? String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; if (revoked) URL.revokeObjectURL(revoked); };
  }, [screenshotId]);

  return { url, loading, error };
}

export function ScreenshotCard({ screenshot, onDeleted }: Props) {
  const { url, loading, error } = useAuthBlobUrl(screenshot.id);
  const [open, setOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const confirm = useConfirm();

  async function handleDelete() {
    if (!onDeleted) return;
    if (!(await confirm({
      title: 'Delete this screenshot?',
      description: 'The image is permanently removed. This cannot be undone.',
      destructive: true,
      confirmLabel: 'Delete',
    }))) return;
    setDeleting(true);
    try {
      await monitoringApi.deleteScreenshot(screenshot.id);
      toast.success('Screenshot deleted');
      setOpen(false);
      onDeleted();
    } catch (err: any) {
      toast.error('Delete failed', { description: err.message });
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <div className="relative">
        <button type="button" onClick={() => setOpen(true)}
          className="w-full border rounded-md overflow-hidden bg-card hover:ring-2 hover:ring-primary/40 text-left">
          <div className="aspect-video bg-muted flex items-center justify-center overflow-hidden">
            {loading && <LoadingState variant="inline" />}
            {error && <div className="text-xs text-red-500 dark:text-red-400 p-2">{error}</div>}
            {url && <img src={url} alt={screenshot.app_title ?? ''} className="w-full h-full object-cover" />}
          </div>
          <div className="px-3 py-2 text-xs flex items-center justify-between">
            <span className="text-muted-foreground">{formatDateTime(screenshot.captured_at)}</span>
            <div className="flex gap-1">
              {screenshot.is_idle    && <span className={statusPill('warning')}><span className={statusDot('warning')} />Idle</span>}
              {screenshot.is_blurred && <span className={statusPill('muted')}><span className={statusDot('muted')} />Blurred</span>}
              {screenshot.outside_hours && <span className={statusPill('destructive')}><span className={statusDot('destructive')} />Off hours</span>}
            </div>
          </div>
          {screenshot.app_title && (
            <div className="px-3 pb-2 text-xs text-muted-foreground truncate">{screenshot.app_title}</div>
          )}
        </button>
        {onDeleted && (
          <button type="button" onClick={handleDelete} disabled={deleting} aria-label="Delete screenshot"
            className="absolute top-2 right-2 h-8 w-8 rounded-full bg-background/80 backdrop-blur ring-1 ring-border flex items-center justify-center text-muted-foreground hover:text-destructive hover:ring-destructive/40 transition-colors disabled:opacity-50">
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl">
          {url && <img src={url} alt={screenshot.app_title ?? ''} className="w-full rounded-md" />}
          <div className="flex items-center justify-between gap-3 pt-2">
            <div className="text-xs text-muted-foreground">
              {formatDateTime(screenshot.captured_at)} · {screenshot.size_bytes} bytes · {screenshot.content_type}
              {screenshot.app_title && <> · {screenshot.app_title}</>}
            </div>
            {onDeleted && (
              <Button variant="outline" size="sm" onClick={handleDelete} disabled={deleting}
                className="gap-1.5 text-destructive hover:text-destructive shrink-0">
                <Trash2 className="h-3.5 w-3.5" />
                {deleting ? 'Deleting…' : 'Delete'}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
